import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useGetCartQuery as useGetShopifyCartQuery } from "@store/services/shopify";
import { useGetUserDetailsQuery } from "@store/services/user";
import { setShopifyCartId } from "@store/slices/cartSlice";
import { type RootState } from "@store/rootReducer";

import { getShopifyCartId } from "./useCart.storage";

/**
 * Reads the current cart from the first-party Shopify backend by the id remembered
 * at creation time, and returns the full RTK Query result (refetch, isFetching, …).
 *
 * Nothing is queried for a logged-out user, and a missing cart surfaces as
 * `data == null` (which the create-vs-add flows rely on).
 */
export const useCart = () => {
  const dispatch = useDispatch();
  const { data: userDetailsData } = useGetUserDetailsQuery();
  const isAuthenticated = userDetailsData?.authenticated === true;
  const userId = userDetailsData?.id;
  // Store-backed so consumers re-render when a cart is created mid-session.
  const shopifyCartId = useSelector((state: RootState) => state.cart.shopifyCartId);

  // Hydrate from the active user's per-user slot once their id is known.
  useEffect(() => {
    if (userId == null) {
      return;
    }
    dispatch(setShopifyCartId(getShopifyCartId(userId)));
  }, [userId, dispatch]);

  return useGetShopifyCartQuery({ cartId: shopifyCartId ?? "" }, { skip: !isAuthenticated || shopifyCartId == null });
};
